/**
 * LIB-EXPLORER-01 (2026-09-24) — Team → Library, browsed like Google Drive /
 * Windows Explorer.
 *
 * Seth: "make the library feel a little bit more like what it feels like
 * browsing Google Drive / windows explorer folder system. Can we upload
 * directly by dragging and dropping? View as list, preview, etc."
 *
 * What Drive / OneDrive / SharePoint / Dropbox share (adopted): a folder tree
 * + breadcrumb, a sortable list with folders on top (a grid for pictures), a
 * preview pane, drag files — or whole folders — from the computer onto any
 * folder to upload, drag items onto a folder to move them, a "…" /
 * right-click menu, replace-with-a-new-version, and a bin (Archived) instead
 * of delete. Skipped: co-editing, desktop sync and per-file sharing — a
 * 15-person shop doesn't need them.
 *
 * Data: a folder is the path on each document ("Service/Triangle Tube");
 * pegasus_library_folder keeps folders that are still empty. Search rides the
 * same library_search index the phones and Ask Pegasus use. Notes (kind
 * "note") live in folders like files and open in the preview pane.
 */
import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type DragEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Archive, ArchiveRestore, ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, Clock, Download, ExternalLink,
  File as FileIcon, FileArchive, FileAudio, FileImage, FileSpreadsheet, FileText, FileVideo, Folder, FolderInput,
  FolderOpen, FolderPlus, FolderUp, LayoutGrid, Library, Link as LinkIcon, List, Loader2, PanelRightClose,
  PanelRightOpen, Pencil, Plus, Presentation, RefreshCw, Search, StickyNote, TriangleAlert, Upload, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { OverflowMenu, type MenuEntry } from "@/components/ui/Menu";
import { InfoTip } from "@/components/InfoTip";
import { NoteEditorModal, NoteViewerModal, NOTES_FOLDER } from "./LibraryNote";
import {
  type LibraryDoc, useLibraryDocs, useLibrarySearch, useLibraryFolders, useCreateLibraryFolder,
  useForgetLibraryFolders, useMoveLibraryFolder, useMoveLibraryDocs, useSetLibraryDocsActive,
  useArchivedLibraryDocs, useLibraryDocText, useRenameLibraryDoc, uploadLibraryFile, replaceLibraryFile,
  useInvalidateLibrary, libraryDocUrl,
} from "./team";

// ── paths ─────────────────────────────────────────────────────────────────────
const ROOT = "";
const parentOf = (p: string) => (p.includes("/") ? p.slice(0, p.lastIndexOf("/")) : ROOT);
const leafOf = (p: string) => (p.includes("/") ? p.slice(p.lastIndexOf("/") + 1) : p);
const joinPath = (parent: string, name: string) => (parent ? `${parent}/${name}` : name);
const isInside = (path: string, folder: string) => path === folder || path.startsWith(`${folder}/`);
const extOf = (name: string) => /\.([a-z0-9]{1,6})$/i.exec(name)?.[1].toLowerCase() ?? "";
const baseOf = (name: string) => name.toLowerCase().replace(/\.[a-z0-9]{1,6}$/i, "").trim();
const prettyPath = (p: string) => (p ? p.split("/").join(" › ") : "Library");
/** Files a computer leaves behind that never belong in the Library. */
const JUNK = /^(\.ds_store|thumbs\.db|desktop\.ini|~\$.*|\._.*)$/i;

// ── file types ────────────────────────────────────────────────────────────────
type DocKind = "note" | "pdf" | "word" | "excel" | "powerpoint" | "image" | "video" | "audio" | "text" | "archive" | "other";
const KIND: Record<DocKind, { icon: typeof FileText; cls: string; label: string }> = {
  note: { icon: StickyNote, cls: "text-amber-500", label: "Note" },
  pdf: { icon: FileText, cls: "text-rose-500", label: "PDF" },
  word: { icon: FileText, cls: "text-blue-600", label: "Word" },
  excel: { icon: FileSpreadsheet, cls: "text-emerald-600", label: "Excel" },
  powerpoint: { icon: Presentation, cls: "text-orange-500", label: "PowerPoint" },
  image: { icon: FileImage, cls: "text-violet-500", label: "Picture" },
  video: { icon: FileVideo, cls: "text-pink-500", label: "Video" },
  audio: { icon: FileAudio, cls: "text-sky-600", label: "Audio" },
  text: { icon: FileText, cls: "text-slate-500", label: "Text" },
  archive: { icon: FileArchive, cls: "text-slate-500", label: "Zip" },
  other: { icon: FileIcon, cls: "text-slate-400", label: "File" },
};
function kindOf(d: { kind?: string | null; mime: string | null; filename: string }): DocKind {
  if (d.kind === "note") return "note";
  const e = extOf(d.filename);
  const m = (d.mime ?? "").toLowerCase();
  if (e === "pdf" || m === "application/pdf") return "pdf";
  if (["doc", "docx", "rtf", "odt", "pages"].includes(e) || m.includes("wordprocessing") || m === "application/msword") return "word";
  if (["xls", "xlsx", "xlsm", "csv", "ods", "numbers"].includes(e) || m.includes("spreadsheet") || m.includes("excel")) return "excel";
  if (["ppt", "pptx", "pps", "ppsx", "odp", "key"].includes(e) || m.includes("presentation") || m.includes("powerpoint")) return "powerpoint";
  if (m.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "heic", "bmp", "tif", "tiff", "svg"].includes(e)) return "image";
  if (m.startsWith("video/") || ["mp4", "mov", "m4v", "webm", "wmv", "avi", "mkv"].includes(e)) return "video";
  if (m.startsWith("audio/") || ["mp3", "wav", "m4a", "aac", "ogg"].includes(e)) return "audio";
  if (["txt", "md", "log", "json", "xml"].includes(e) || m.startsWith("text/")) return "text";
  if (["zip", "rar", "7z", "gz", "tar"].includes(e)) return "archive";
  return "other";
}
/** Videos a browser can actually play (WMV can't). */
const PLAYABLE_VIDEO = new Set(["mp4", "m4v", "webm", "mov", "ogv"]);
const driveOnly = (d: LibraryDoc) => d.kind !== "note" && !d.storage_path && !!d.external_url;

// ── dates, sizes ──────────────────────────────────────────────────────────────
const modifiedOf = (d: LibraryDoc) => d.updated_at ?? d.created_at;
function fmtWhen(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString("en-US", d.getFullYear() === now.getFullYear()
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" });
}
const fmtFull = (ts: string | null | undefined) =>
  ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";
function fmtBytes(n: number | null | undefined): string {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}
const shortWho = (email: string | null | undefined) => (email ? email.split("@")[0] : "");

const errText = (e: unknown) =>
  e instanceof Error ? e.message : typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : String(e);
function uploadError(e: unknown): string {
  const m = errText(e);
  if (/exceed|too large|maximum allowed size|payload|413/i.test(m)) return "Too big to upload (storage limit)";
  return m.slice(0, 120);
}
/** Search snippets come back with <b> around the matched words — keep only those. */
const safeSnippet = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/&lt;(\/?)b&gt;/g, "<$1b>");

// ── per-person view settings (a convenience — fine if storage is off) ─────────
function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = window.localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}
function writePref(key: string, v: string) {
  try { window.localStorage.setItem(key, v); } catch { /* private window */ }
}
const PREF_VIEW = "pegasus.library.view";
const PREF_PREVIEW = "pegasus.library.preview";

// ── the folder tree ───────────────────────────────────────────────────────────
interface FolderNode { path: string; name: string; children: FolderNode[]; direct: number; total: number; modified: string | null }
function buildTree(docs: LibraryDoc[], extra: string[]) {
  const byPath = new Map<string, FolderNode>();
  const root: FolderNode = { path: ROOT, name: "Library", children: [], direct: 0, total: 0, modified: null };
  byPath.set(ROOT, root);
  const ensure = (p: string): FolderNode => {
    const hit = byPath.get(p);
    if (hit) return hit;
    const node: FolderNode = { path: p, name: leafOf(p), children: [], direct: 0, total: 0, modified: null };
    byPath.set(p, node);
    ensure(parentOf(p)).children.push(node);
    return node;
  };
  for (const p of extra) if (p) ensure(p);
  for (const d of docs) {
    const n = ensure(d.folder || "General");
    n.direct += 1;
    const m = modifiedOf(d);
    if (!n.modified || m > n.modified) n.modified = m;
  }
  const sum = (n: FolderNode): number => {
    n.children.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
    let t = n.direct;
    for (const c of n.children) {
      t += sum(c);
      if (c.modified && (!n.modified || c.modified > n.modified)) n.modified = c.modified;
    }
    n.total = t;
    return t;
  };
  sum(root);
  return { root, byPath };
}

// ── drag & drop from the computer ─────────────────────────────────────────────
interface Picked { file: File; rel: string }
/** Every file in a drop — whole folders included, keeping their subfolders.
 *  Must be called synchronously inside the drop event (it reads the items
 *  before the first await). */
async function filesFromDrop(dt: DataTransfer): Promise<Picked[]> {
  const entries: FileSystemEntry[] = [];
  for (const it of Array.from(dt.items ?? [])) {
    if (it.kind !== "file") continue;
    const en = it.webkitGetAsEntry?.();
    if (en) entries.push(en);
  }
  if (!entries.length) return Array.from(dt.files ?? []).map((file) => ({ file, rel: ROOT }));
  const out: Picked[] = [];
  const walk = async (en: FileSystemEntry, dir: string): Promise<void> => {
    if (en.isFile) {
      const file = await new Promise<File>((res, rej) => (en as FileSystemFileEntry).file(res, rej));
      out.push({ file, rel: dir });
      return;
    }
    if (!en.isDirectory) return;
    const sub = joinPath(dir, en.name);
    const reader = (en as FileSystemDirectoryEntry).createReader();
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
      if (!batch.length) break;
      for (const child of batch) await walk(child, sub);
    }
  };
  for (const en of entries) await walk(en, ROOT);
  return out;
}
function filesFromInput(list: FileList | null): Picked[] {
  return Array.from(list ?? []).map((file) => {
    const rel = file.webkitRelativePath ?? "";
    return { file, rel: rel.includes("/") ? rel.slice(0, rel.lastIndexOf("/")) : ROOT };
  });
}

// ── rows ─────────────────────────────────────────────────────────────────────
type Loc = { kind: "folder"; path: string } | { kind: "recent" } | { kind: "archived" };
type SortKey = "name" | "modified" | "type" | "size";
type FolderRow = { type: "folder"; key: string; path: string; name: string; count: number; modified: string | null };
type DocRow = { type: "doc"; key: string; doc: LibraryDoc; snippet?: string | null };
type Row = FolderRow | DocRow;
const isDocRow = (r: Row): r is DocRow => r.type === "doc";
const isFolderRow = (r: Row): r is FolderRow => r.type === "folder";

interface Job { id: number; name: string; folder: string; state: "wait" | "up" | "done" | "fail"; note?: string }
interface Conflict { id: number; file: File; folder: string; doc: LibraryDoc; choice: "replace" | "both" | "skip" }
interface Toast { text: string; undo?: () => void }

const DRAG_TYPE = "application/x-pegasus-library";
let seq = 0;

function TypeIcon({ row, big }: { row: Row; big?: boolean }) {
  if (row.type === "folder") {
    return <Folder className={cn("shrink-0 fill-amber-100 text-amber-500", big ? "h-12 w-12" : "h-[18px] w-[18px]")} />;
  }
  const k = KIND[kindOf(row.doc)];
  const Icon = k.icon;
  return <Icon className={cn("shrink-0", k.cls, big ? "h-11 w-11" : "h-[18px] w-[18px]")} />;
}

/** Inline name editor — Enter saves, Esc cancels, clicking away saves. */
function RenameInput({ initial, selectBase, onDone, onCancel }: {
  initial: string; selectBase?: boolean; onDone: (v: string) => void; onCancel: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const cut = selectBase ? initial.lastIndexOf(".") : -1;
    el.setSelectionRange(0, cut > 0 ? cut : initial.length);
  }, [initial, selectBase]);
  const finish = (v: string | null) => {
    if (done.current) return;
    done.current = true;
    if (v == null) onCancel(); else onDone(v);
  };
  return (
    <input ref={ref} defaultValue={initial}
      onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") finish(e.currentTarget.value);
        if (e.key === "Escape") finish(null);
      }}
      onBlur={(e) => finish(e.currentTarget.value)}
      className="w-full min-w-0 rounded-md border border-brand-400 bg-white px-1.5 py-0.5 text-[13px] text-slate-800 outline-none ring-2 ring-brand-500/20" />
  );
}

/** Right-click menu at the pointer (same look as the "…" menus). */
function ContextMenu({ x, y, items, onClose }: { x: number; y: number; items: MenuEntry[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)), top: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)) });
  }, [x, y]);
  useEffect(() => {
    const down = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const away = () => onClose();
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key);
    window.addEventListener("scroll", away, true);
    window.addEventListener("resize", away);
    window.addEventListener("blur", away);
    const t = window.setTimeout(() => ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus(), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", down);
      document.removeEventListener("keydown", key);
      window.removeEventListener("scroll", away, true);
      window.removeEventListener("resize", away);
      window.removeEventListener("blur", away);
    };
  }, [onClose]);
  // no divider first, last, or twice in a row
  const clean = items.filter((it, i, all) => it !== "divider" || (i > 0 && i < all.length - 1 && all[i - 1] !== "divider"));
  return createPortal(
    <div ref={ref} role="menu" data-glass="panel" data-stay-open=""
      style={{ position: "fixed", left: pos.left, top: pos.top, zIndex: 1000 }}
      className="min-w-[210px] max-w-[300px] overflow-hidden rounded-xl py-1"
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
        const els = Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []);
        if (!els.length) return;
        e.preventDefault();
        const i = els.indexOf(document.activeElement as HTMLButtonElement);
        els[e.key === "ArrowDown" ? (i + 1) % els.length : (i - 1 + els.length) % els.length]?.focus();
      }}>
      {clean.map((it, i) =>
        it === "divider" ? (
          <div key={`d${i}`} role="separator" className="my-1 border-t border-hairline" />
        ) : (
          <button key={it.key ?? it.label} type="button" role="menuitem" disabled={it.disabled} title={it.title}
            onClick={() => { onClose(); it.onSelect(); }}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-black/[0.04] focus-visible:bg-black/[0.04] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40",
              it.danger ? "text-rose-700" : "text-slate-700",
            )}>
            {it.icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-3.5 [&>svg]:w-3.5">{it.icon}</span>}
            <span className="min-w-0 flex-1 truncate">{it.label}</span>
          </button>
        ),
      )}
    </div>,
    document.body,
  );
}

/** Signed links for the pictures on screen (grid view thumbnails). */
function useThumbs(paths: string[]) {
  const key = paths.join("|");
  return useQuery({
    queryKey: ["library_thumbs", key],
    enabled: paths.length > 0,
    staleTime: 8 * 60 * 1000,
    queryFn: async () => {
      const m = new Map<string, string>();
      const { data } = await supabase.storage.from("customer-files").createSignedUrls(paths, 900);
      for (const r of data ?? []) if (r.path && r.signedUrl) m.set(r.path, r.signedUrl);
      return m;
    },
  });
}

// ══════════════════════════════════════════════════════════════════════════════
export function LibraryExplorer() {
  const [params, setParams] = useSearchParams();
  const docsQ = useLibraryDocs();
  const docs = useMemo(() => docsQ.data ?? [], [docsQ.data]);
  const emptyFoldersQ = useLibraryFolders();
  const emptyFolders = useMemo(() => emptyFoldersQ.data ?? [], [emptyFoldersQ.data]);
  const invalidate = useInvalidateLibrary();
  const createFolder = useCreateLibraryFolder();
  const forgetFolders = useForgetLibraryFolders();
  const moveFolder = useMoveLibraryFolder();
  const moveDocs = useMoveLibraryDocs();
  const setActive = useSetLibraryDocsActive();
  const renameDoc = useRenameLibraryDoc();

  // ── where we are (in the URL, so Back and links work) ──
  const loc: Loc = useMemo(() => {
    const v = params.get("view");
    if (v === "recent") return { kind: "recent" };
    if (v === "archived") return { kind: "archived" };
    return { kind: "folder", path: (params.get("folder") ?? ROOT).replace(/^\/+|\/+$/g, "") };
  }, [params]);
  const currentPath = loc.kind === "folder" ? loc.path : null;
  const q = params.get("q") ?? "";
  const searching = q.trim().length >= 2;
  const hits = useLibrarySearch(q);
  const archivedQ = useArchivedLibraryDocs(loc.kind === "archived");
  const archived = archivedQ.data;

  const [view, setViewState] = useState<"list" | "grid">(() => readPref(PREF_VIEW, ["list", "grid"] as const, "list"));
  const setView = (v: "list" | "grid") => { setViewState(v); writePref(PREF_VIEW, v); };
  const [showPreview, setShowPreviewState] = useState(() => readPref(PREF_PREVIEW, ["on", "off"] as const, "on") === "on");
  const setShowPreview = (on: boolean) => { setShowPreviewState(on); writePref(PREF_PREVIEW, on ? "on" : "off"); };
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "name", dir: 1 });

  const { root, byPath } = useMemo(() => buildTree(docs, emptyFolders), [docs, emptyFolders]);
  const docById = useMemo(() => new Map(docs.map((d) => [d.id, d])), [docs]);
  const allFolderPaths = useMemo(() => [...byPath.keys()].filter(Boolean).sort((a, b) => a.localeCompare(b)), [byPath]);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([ROOT]));
  useEffect(() => {
    if (currentPath == null) return;
    setExpanded((s) => {
      const n = new Set(s);
      for (let p = currentPath; p; p = parentOf(p)) n.add(p);
      return n.size === s.size ? s : n;
    });
  }, [currentPath]);
  const toggleExpanded = (p: string) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(p)) n.delete(p); else n.add(p);
    return n;
  });

  // ── selection ──
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [focus, setFocus] = useState<string | null>(null);
  const anchorRef = useRef<string | null>(null);
  const pendingFocus = useRef<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number; empty: boolean } | null>(null);
  const closeCtx = useCallback(() => setCtx(null), []);
  const [moveOpen, setMoveOpen] = useState<{ docs: string[]; folders: string[] } | null>(null);
  const [archiveAsk, setArchiveAsk] = useState<{ docs: string[]; folders: string[] } | null>(null);
  const [noteEdit, setNoteEdit] = useState<{ id: string | null } | null>(null);
  const [noteView, setNoteView] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [trayOpen, setTrayOpen] = useState(true);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const dropKeyRef = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const filesInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const replaceInput = useRef<HTMLInputElement>(null);
  const replaceFor = useRef<LibraryDoc | null>(null);

  const toastTimer = useRef<number | null>(null);
  const say = useCallback((text: string, undo?: () => void) => {
    setToast({ text, undo });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), undo ? 9000 : 5000);
  }, []);

  const go = useCallback((next: Loc) => {
    const p = new URLSearchParams(params);
    p.set("pane", "library");
    p.delete("q");
    p.delete("doc");
    if (next.kind === "folder") {
      p.delete("view");
      if (next.path) p.set("folder", next.path); else p.delete("folder");
    } else {
      p.delete("folder");
      p.set("view", next.kind);
    }
    setParams(p);
    setSelected(new Set());
    setFocus(null);
    setRenaming(null);
    setCreatingFolder(false);
    anchorRef.current = null;
  }, [params, setParams]);
  const setQ = (v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set("q", v); else p.delete("q");
    setParams(p, { replace: true });
  };

  // ── what's on screen ──
  const rows: Row[] = useMemo(() => {
    const folderRow = (n: FolderNode): Row => ({ type: "folder", key: `f:${n.path}`, path: n.path, name: n.name, count: n.total, modified: n.modified });
    const docRow = (d: LibraryDoc, snippet?: string | null): Row => ({ type: "doc", key: `d:${d.id}`, doc: d, snippet });
    if (searching) {
      const ql = q.trim().toLowerCase();
      const folderHits = [...byPath.values()].filter((n) => n.path && n.name.toLowerCase().includes(ql)).slice(0, 12);
      const byName = docs.filter((d) => d.filename.toLowerCase().includes(ql));
      const seen = new Set(byName.map((d) => d.id));
      const inside: Row[] = [];
      for (const h of hits.data ?? []) {
        const d = docById.get(h.id);
        if (d && !seen.has(d.id)) { seen.add(d.id); inside.push(docRow(d, h.snippet)); }
      }
      return [...folderHits.map(folderRow), ...byName.slice(0, 80).map((d) => docRow(d)), ...inside];
    }
    if (loc.kind === "recent") {
      return [...docs].sort((a, b) => modifiedOf(b).localeCompare(modifiedOf(a))).slice(0, 60).map((d) => docRow(d));
    }
    if (loc.kind === "archived") return (archived ?? []).map((d) => docRow(d));
    const node = byPath.get(loc.path);
    return [...(node?.children ?? []).map(folderRow), ...docs.filter((d) => d.folder === loc.path).map((d) => docRow(d))];
  }, [searching, q, byPath, docs, hits.data, docById, loc, archived]);

  const sortable = !searching && loc.kind !== "recent";
  const shown: Row[] = useMemo(() => {
    if (!sortable) return rows;
    const nameCmp = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
    const folders = rows.filter(isFolderRow).sort((a, b) => (sort.key === "name" ? sort.dir : 1) * nameCmp(a.name, b.name));
    const files = rows.filter(isDocRow).sort((a, b) => {
      const A = a.doc;
      const B = b.doc;
      let c = 0;
      if (sort.key === "modified") c = modifiedOf(A).localeCompare(modifiedOf(B));
      else if (sort.key === "size") c = (A.size_bytes ?? -1) - (B.size_bytes ?? -1);
      else if (sort.key === "type") c = nameCmp(KIND[kindOf(A)].label, KIND[kindOf(B)].label);
      if (c === 0) c = nameCmp(A.filename, B.filename);
      return c * sort.dir;
    });
    return [...folders, ...files];
  }, [rows, sortable, sort]);

  const selectedRows = useMemo(() => shown.filter((r) => selected.has(r.key)), [shown, selected]);
  const focusRow = useMemo(() => shown.find((r) => r.key === focus) ?? null, [shown, focus]);

  const scrollToKey = (k: string) =>
    listRef.current?.querySelector(`[data-key="${CSS.escape(k)}"]`)?.scrollIntoView({ block: "nearest" });

  // A pending pick (after "Show in folder", a new note, a ?doc= link) lands
  // once its row is on screen.
  useEffect(() => {
    const k = pendingFocus.current;
    if (!k || !shown.some((r) => r.key === k)) return;
    pendingFocus.current = null;
    setSelected(new Set([k]));
    setFocus(k);
    anchorRef.current = k;
    window.requestAnimationFrame(() => scrollToKey(k));
  }, [shown]);

  // ?doc=<id> — open the folder a document lives in, with it picked.
  const docParam = params.get("doc");
  const appliedDoc = useRef<string | null>(null);
  useEffect(() => {
    if (!docParam || appliedDoc.current === docParam || !docsQ.data) return;
    appliedDoc.current = docParam;
    const d = docById.get(docParam);
    const p = new URLSearchParams(params);
    p.delete("doc");
    if (!d) {
      setParams(p, { replace: true });
      say("That document isn't in the Library any more — it may be archived.");
      return;
    }
    p.set("pane", "library");
    p.delete("view");
    p.delete("q");
    p.set("folder", d.folder);
    setParams(p, { replace: true });
    pendingFocus.current = `d:${d.id}`;
  }, [docParam, docsQ.data, docById, params, setParams, say]);

  // Ask Pegasus links arrive as ?q=<document name> — pick that document.
  const autoPicked = useRef<string | null>(null);
  useEffect(() => {
    if (!searching || autoPicked.current === q) return;
    const want = baseOf(q);
    const hit = shown.find((r) => r.type === "doc" && baseOf(r.doc.filename) === want);
    if (!hit) return;
    autoPicked.current = q;
    setSelected(new Set([hit.key]));
    setFocus(hit.key);
    anchorRef.current = hit.key;
  }, [searching, q, shown]);

  // Files dropped where nothing catches them must never navigate the tab away.
  useEffect(() => {
    const isFiles = (e: globalThis.DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files");
    const stop = (e: globalThis.DragEvent) => { if (isFiles(e)) e.preventDefault(); };
    const end = () => { dropKeyRef.current = null; setDropKey(null); };
    window.addEventListener("dragover", stop);
    window.addEventListener("drop", stop);
    window.addEventListener("dragend", end);
    return () => {
      window.removeEventListener("dragover", stop);
      window.removeEventListener("drop", stop);
      window.removeEventListener("dragend", end);
    };
  }, []);

  // ── opening, downloading ──
  const openDoc = useCallback(async (d: LibraryDoc) => {
    if (d.kind === "note" || (!d.storage_path && !d.external_url)) { setNoteView(d.id); return; }
    if (!d.storage_path) {
      if (d.external_url) window.open(d.external_url, "_blank", "noopener");
      return;
    }
    // Open the tab inside the click (pop-up blockers), then point it at the file.
    const w = window.open("about:blank", "_blank");
    const url = await libraryDocUrl(d.storage_path);
    if (!w) {
      say("The browser blocked the new tab — allow pop-ups for Pegasus.");
      return;
    }
    w.opener = null;
    if (url) w.location.href = url; else w.close();
  }, [say]);

  const openRow = (r: Row) => {
    if (r.type === "folder") { go({ kind: "folder", path: r.path }); return; }
    if (r.doc.kind === "note") {
      if (r.doc.active) setNoteEdit({ id: r.doc.id }); else setNoteView(r.doc.id);
      return;
    }
    void openDoc(r.doc);
  };

  const download = async (list: LibraryDoc[]) => {
    const files = list.filter((d) => d.storage_path);
    if (!files.length) { say("Nothing to download — notes and Drive links have no file here."); return; }
    for (const d of files) {
      const url = await libraryDocUrl(d.storage_path as string, d.filename);
      if (!url) continue;
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      a.download = d.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (files.length > 1) await new Promise((res) => window.setTimeout(res, 400));
    }
  };

  const copyLink = (t: { doc?: string; folder?: string }) => {
    const u = new URL(`${window.location.origin}/team`);
    u.searchParams.set("pane", "library");
    if (t.doc) u.searchParams.set("doc", t.doc);
    if (t.folder) u.searchParams.set("folder", t.folder);
    navigator.clipboard?.writeText(u.toString()).then(
      () => say("Link copied — anyone signed in to Pegasus can open it."),
      () => say("Couldn't copy the link."),
    );
  };

  const showInFolder = (d: LibraryDoc) => {
    pendingFocus.current = `d:${d.id}`;
    go({ kind: "folder", path: d.folder });
  };

  // ── uploads ──
  const runUploads = useCallback((items: { file: File; folder: string; replace?: LibraryDoc }[]) => {
    if (!items.length) return;
    const mine: Job[] = items.map((it) => ({ id: ++seq, name: it.file.name, folder: it.replace?.folder ?? it.folder, state: "wait" }));
    setJobs((js) => [...js.filter((j) => j.state === "wait" || j.state === "up"), ...mine]);
    setTrayOpen(true);
    const patch = (id: number, p: Partial<Job>) => setJobs((js) => js.map((j) => (j.id === id ? { ...j, ...p } : j)));
    let next = 0;
    let finished = 0;
    const worker = async () => {
      while (next < items.length) {
        const k = next++;
        const it = items[k];
        const job = mine[k];
        patch(job.id, { state: "up" });
        try {
          if (it.replace) {
            const r = await replaceLibraryFile(it.replace, it.file);
            patch(job.id, { state: "done", note: `Replaced — now v${r.version}` });
          } else {
            await uploadLibraryFile(it.file, it.folder);
            patch(job.id, { state: "done" });
          }
        } catch (e) {
          patch(job.id, { state: "fail", note: uploadError(e) });
        }
        finished += 1;
        if (finished % 8 === 0) void invalidate();
      }
    };
    void Promise.all([worker(), worker(), worker()]).then(() => invalidate());
  }, [invalidate]);

  /** Queue files for upload; same-name files wait for a Replace / Keep both / Skip answer. */
  const enqueue = useCallback((list: { file: File; folder: string }[]) => {
    const clean = list.filter(({ file, folder }) => folder && !JUNK.test(file.name) && file.size > 0);
    const skipped = list.length - clean.length;
    if (!clean.length) {
      if (list.length) say("Nothing to upload — those were empty or system files.");
      return;
    }
    const plain: { file: File; folder: string }[] = [];
    const conf: Conflict[] = [];
    for (const it of clean) {
      const lower = it.file.name.toLowerCase();
      const b = baseOf(it.file.name);
      const same =
        docs.find((d) => d.kind !== "note" && d.folder === it.folder && (d.filename.toLowerCase() === lower || (!extOf(d.filename) && baseOf(d.filename) === b)))
        // A file still living in Google Drive merges with its real file wherever it's dropped.
        ?? docs.find((d) => driveOnly(d) && baseOf(d.filename) === b);
      if (same) conf.push({ id: ++seq, file: it.file, folder: it.folder, doc: same, choice: "replace" });
      else plain.push(it);
    }
    runUploads(plain);
    if (conf.length) setConflicts((c) => [...c, ...conf]);
    if (skipped) say(`Skipped ${skipped} empty or system file${skipped === 1 ? "" : "s"}.`);
  }, [docs, runUploads, say]);

  const askReplace = (doc: LibraryDoc, file: File) =>
    setConflicts((c) => [...c, { id: ++seq, file, folder: doc.folder, doc, choice: "replace" }]);
  const pickReplace = (doc: LibraryDoc) => {
    replaceFor.current = doc;
    replaceInput.current?.click();
  };
  const resolveConflicts = (apply: boolean) => {
    const list = conflicts;
    setConflicts([]);
    if (!apply) return;
    runUploads(list.filter((c) => c.choice !== "skip").map((c) =>
      c.choice === "replace" ? { file: c.file, folder: c.doc.folder, replace: c.doc } : { file: c.file, folder: c.folder }));
  };

  // Clear a finished upload tray on its own after a moment (failures stay).
  const busy = jobs.some((j) => j.state === "wait" || j.state === "up");
  const failed = jobs.filter((j) => j.state === "fail").length;
  useEffect(() => {
    if (!jobs.length || busy || failed) return;
    const t = window.setTimeout(() => setJobs([]), 6000);
    return () => window.clearTimeout(t);
  }, [jobs, busy, failed]);

  // ── moving, renaming, archiving ──
  const moveItems = async (docIds: string[], folderPaths: string[], target: string) => {
    if (loc.kind === "archived") return;
    const blocked = folderPaths.some((f) => isInside(target, f));
    const folders = folderPaths.filter((f) => !isInside(target, f) && parentOf(f) !== target);
    const ids = target === ROOT ? [] : docIds.filter((id) => (docById.get(id)?.folder ?? target) !== target);
    if (target === ROOT && docIds.length) say("Documents need a folder — drop them onto one.");
    else if (blocked) say("A folder can't go inside itself.");
    if (!ids.length && !folders.length) return;
    const before = new Map<string, string[]>();
    for (const id of ids) {
      const f = docById.get(id)?.folder ?? "";
      before.set(f, [...(before.get(f) ?? []), id]);
    }
    const moved = folders.map((f) => ({ from: f, to: joinPath(target, leafOf(f)) }));
    try {
      if (ids.length) await moveDocs.mutateAsync({ ids, folder: target });
      for (const m of moved) await moveFolder.mutateAsync(m);
      setSelected(new Set());
      const n = ids.length + moved.length;
      say(`Moved ${n} item${n === 1 ? "" : "s"} to ${target ? leafOf(target) : "Library"}.`, () => {
        void (async () => {
          for (const [f, list] of before) await moveDocs.mutateAsync({ ids: list, folder: f });
          for (const m of moved) await moveFolder.mutateAsync({ from: m.to, to: m.from });
          say("Move undone.");
        })();
      });
    } catch (e) {
      say(`Couldn't move — ${errText(e)}`);
    }
  };

  const commitRename = async (r: Row, raw: string) => {
    setRenaming(null);
    const name = raw.trim().replace(/\s+/g, " ");
    if (!name) return;
    if (r.type === "folder") {
      if (name === r.name) return;
      if (name.includes("/")) { say("Folder names can't have a / in them."); return; }
      const to = joinPath(parentOf(r.path), name);
      if (byPath.has(to)) { say(`There's already a folder called "${name}" here.`); return; }
      try {
        await moveFolder.mutateAsync({ from: r.path, to });
        if (currentPath && isInside(currentPath, r.path)) go({ kind: "folder", path: to + currentPath.slice(r.path.length) });
        else pendingFocus.current = `f:${to}`;
      } catch (e) {
        say(`Couldn't rename — ${errText(e)}`);
      }
      return;
    }
    const d = r.doc;
    let next = name;
    const oldExt = extOf(d.filename);
    if (oldExt && !extOf(next) && d.kind !== "note") next = `${next}.${oldExt}`;
    if (next === d.filename) return;
    try {
      await renameDoc.mutateAsync({ id: d.id, filename: next });
    } catch (e) {
      say(`Couldn't rename — ${errText(e)}`);
    }
  };

  const commitNewFolder = async (raw: string) => {
    setCreatingFolder(false);
    const name = raw.trim().replace(/\s+/g, " ");
    if (!name || currentPath == null) return;
    if (name.includes("/")) { say("Folder names can't have a / in them."); return; }
    const path = joinPath(currentPath, name);
    pendingFocus.current = `f:${path}`;
    if (byPath.has(path)) { say(`"${name}" is already here.`); return; }
    try {
      await createFolder.mutateAsync(path);
    } catch (e) {
      say(`Couldn't make the folder — ${errText(e)}`);
    }
  };
  const startNewFolder = () => {
    if (currentPath == null) return;
    if (searching) setQ("");
    setCreatingFolder(true);
    listRef.current?.scrollTo({ top: 0 });
  };

  const subtreeDocIds = (path: string) => docs.filter((d) => isInside(d.folder, path)).map((d) => d.id);
  const archiveItems = (docIds: string[], folderPaths: string[]) => {
    const inFolders = folderPaths.flatMap(subtreeDocIds);
    const all = [...new Set([...docIds, ...inFolders])];
    if (folderPaths.length && inFolders.length) { setArchiveAsk({ docs: all, folders: folderPaths }); return; }
    void doArchive(all, folderPaths);
  };
  const doArchive = async (ids: string[], folderPaths: string[]) => {
    const recs = [...new Set(folderPaths.flatMap((f) => emptyFolders.filter((p) => isInside(p, f))))];
    try {
      if (ids.length) await setActive.mutateAsync({ ids, active: false });
      if (recs.length) await forgetFolders.mutateAsync(recs);
      setSelected(new Set());
      setFocus(null);
      const text = ids.length
        ? `Archived ${ids.length} document${ids.length === 1 ? "" : "s"}.`
        : `Removed ${folderPaths.length === 1 ? "the empty folder" : `${folderPaths.length} empty folders`}.`;
      say(text, () => {
        void (async () => {
          if (ids.length) await setActive.mutateAsync({ ids, active: true });
          for (const p of recs) await createFolder.mutateAsync(p);
          say("Brought back.");
        })();
      });
      if (currentPath && folderPaths.some((f) => isInside(currentPath, f))) go({ kind: "folder", path: parentOf(folderPaths[0]) });
    } catch (e) {
      say(`Couldn't archive — ${errText(e)}`);
    }
  };
  const restore = async (list: LibraryDoc[]) => {
    if (!list.length) return;
    try {
      await setActive.mutateAsync({ ids: list.map((d) => d.id), active: true });
      setSelected(new Set());
      setFocus(null);
      say(list.length === 1 ? `Restored — back in ${prettyPath(list[0].folder)}.` : `Restored ${list.length} documents.`);
    } catch (e) {
      say(`Couldn't restore — ${errText(e)}`);
    }
  };

  // ── menus ──
  const folderActions = (): MenuEntry[] => [
    { key: "newfolder", label: "New folder", icon: <FolderPlus />, onSelect: startNewFolder, disabled: currentPath == null },
    { key: "files", label: "Upload files…", icon: <Upload />, onSelect: () => filesInput.current?.click(), disabled: !currentPath, title: currentPath ? undefined : "Open a folder first" },
    { key: "folderup", label: "Upload a folder…", icon: <FolderUp />, onSelect: () => folderInput.current?.click(), disabled: currentPath == null },
    "divider",
    { key: "note", label: "New note", icon: <StickyNote />, onSelect: () => setNoteEdit({ id: null }) },
  ];

  const actionsFor = (sel: Row[]): MenuEntry[] => {
    if (!sel.length) return [];
    const docsSel = sel.filter(isDocRow).map((r) => r.doc);
    const foldersSel = sel.filter(isFolderRow).map((r) => r.path);
    const withFiles = docsSel.filter((d) => d.storage_path).length;
    if (loc.kind === "archived") {
      return [
        { key: "restore", label: sel.length > 1 ? `Restore ${sel.length} documents` : "Restore", icon: <ArchiveRestore />, onSelect: () => void restore(docsSel) },
        { key: "dl", label: "Download", icon: <Download />, onSelect: () => void download(docsSel), disabled: !withFiles },
      ];
    }
    if (sel.length > 1) {
      return [
        { key: "move", label: `Move ${sel.length} items to…`, icon: <FolderInput />, onSelect: () => setMoveOpen({ docs: docsSel.map((d) => d.id), folders: foldersSel }) },
        { key: "dl", label: withFiles ? `Download ${withFiles} file${withFiles === 1 ? "" : "s"}` : "Download", icon: <Download />, onSelect: () => void download(docsSel), disabled: !withFiles },
        "divider",
        { key: "archive", label: `Archive ${sel.length} items`, icon: <Archive />, danger: true, onSelect: () => archiveItems(docsSel.map((d) => d.id), foldersSel) },
      ];
    }
    const r = sel[0];
    if (r.type === "folder") {
      return [
        { key: "open", label: "Open", icon: <FolderOpen />, onSelect: () => go({ kind: "folder", path: r.path }) },
        { key: "rename", label: "Rename", icon: <Pencil />, onSelect: () => setRenaming(r.key) },
        { key: "move", label: "Move to…", icon: <FolderInput />, onSelect: () => setMoveOpen({ docs: [], folders: [r.path] }) },
        { key: "link", label: "Copy link", icon: <LinkIcon />, onSelect: () => copyLink({ folder: r.path }) },
        "divider",
        { key: "archive", label: r.count ? "Archive folder…" : "Remove empty folder", icon: <Archive />, danger: true, onSelect: () => archiveItems([], [r.path]) },
      ];
    }
    const d = r.doc;
    const isNote = d.kind === "note";
    const drive = driveOnly(d);
    const out: MenuEntry[] = [
      { key: "open", label: isNote ? "Open" : drive ? "Open in Google Drive" : "Open in a new tab", icon: <ExternalLink />, onSelect: () => void openDoc(d) },
    ];
    if (isNote) out.push({ key: "edit", label: "Edit note", icon: <Pencil />, onSelect: () => setNoteEdit({ id: d.id }) });
    if (d.storage_path) out.push({ key: "dl", label: "Download", icon: <Download />, onSelect: () => void download([d]) });
    if (!isNote) out.push({ key: "replace", label: drive ? "Bring in the real file…" : "Replace with a new version…", icon: <RefreshCw />, onSelect: () => pickReplace(d) });
    if (searching || loc.kind === "recent") out.push({ key: "show", label: "Show in folder", icon: <FolderOpen />, onSelect: () => showInFolder(d) });
    out.push("divider");
    if (!isNote) out.push({ key: "rename", label: "Rename", icon: <Pencil />, onSelect: () => setRenaming(r.key) });
    out.push(
      { key: "move", label: "Move to…", icon: <FolderInput />, onSelect: () => setMoveOpen({ docs: [d.id], folders: [] }) },
      { key: "link", label: "Copy link", icon: <LinkIcon />, onSelect: () => copyLink({ doc: d.id }) },
      "divider",
      { key: "archive", label: "Archive", icon: <Archive />, danger: true, onSelect: () => archiveItems([d.id], []) },
    );
    return out;
  };

  // ── pointer + keyboard ──
  const clickRow = (e: ReactMouseEvent, r: Row) => {
    e.stopPropagation();
    if (e.shiftKey && anchorRef.current) {
      const a = shown.findIndex((x) => x.key === anchorRef.current);
      const b = shown.findIndex((x) => x.key === r.key);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        setSelected(new Set(shown.slice(lo, hi + 1).map((x) => x.key)));
      }
    } else if (e.metaKey || e.ctrlKey) {
      setSelected((s) => {
        const n = new Set(s);
        if (n.has(r.key)) n.delete(r.key); else n.add(r.key);
        return n;
      });
      anchorRef.current = r.key;
    } else {
      setSelected(new Set([r.key]));
      anchorRef.current = r.key;
    }
    setFocus(r.key);
    listRef.current?.focus({ preventScroll: true });
  };
  const toggleCheck = (e: ReactMouseEvent, r: Row) => {
    e.stopPropagation();
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(r.key)) n.delete(r.key); else n.add(r.key);
      return n;
    });
    anchorRef.current = r.key;
    setFocus(r.key);
  };
  const rowCtx = (e: ReactMouseEvent, r: Row) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selected.has(r.key)) { setSelected(new Set([r.key])); anchorRef.current = r.key; }
    setFocus(r.key);
    setCtx({ x: e.clientX, y: e.clientY, empty: false });
  };
  const emptyCtx = (e: ReactMouseEvent) => {
    if (currentPath == null || searching) return;
    e.preventDefault();
    setSelected(new Set());
    setFocus(null);
    setCtx({ x: e.clientX, y: e.clientY, empty: true });
  };

  const onListKey = (e: ReactKeyboardEvent) => {
    if (renaming || creatingFolder) return;
    if ((e.target as HTMLElement).closest("input,textarea,select,[role='menu']")) return;
    const idx = focus ? shown.findIndex((r) => r.key === focus) : -1;
    const moveTo = (n: number, extend: boolean) => {
      const r = shown[Math.max(0, Math.min(shown.length - 1, n))];
      if (!r) return;
      if (extend && anchorRef.current) {
        const a = shown.findIndex((x) => x.key === anchorRef.current);
        const b = shown.indexOf(r);
        const [lo, hi] = a < b ? [a, b] : [b, a];
        setSelected(new Set(shown.slice(Math.max(0, lo), hi + 1).map((x) => x.key)));
      } else {
        setSelected(new Set([r.key]));
        anchorRef.current = r.key;
      }
      setFocus(r.key);
      scrollToKey(r.key);
    };
    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        e.preventDefault(); moveTo(idx + 1, e.shiftKey); break;
      case "ArrowUp":
      case "ArrowLeft":
        e.preventDefault(); moveTo(idx < 0 ? 0 : idx - 1, e.shiftKey); break;
      case "Home":
        e.preventDefault(); moveTo(0, e.shiftKey); break;
      case "End":
        e.preventDefault(); moveTo(shown.length - 1, e.shiftKey); break;
      case "Enter":
        if (focusRow) { e.preventDefault(); openRow(focusRow); }
        break;
      case "Backspace":
        if (currentPath) { e.preventDefault(); go({ kind: "folder", path: parentOf(currentPath) }); }
        break;
      case "F2":
        if (focusRow && loc.kind !== "archived" && !(focusRow.type === "doc" && focusRow.doc.kind === "note")) {
          e.preventDefault(); setRenaming(focusRow.key);
        }
        break;
      case "Delete":
        if (selectedRows.length && loc.kind !== "archived") {
          e.preventDefault();
          archiveItems(selectedRows.filter(isDocRow).map((r) => r.doc.id), selectedRows.filter(isFolderRow).map((r) => r.path));
        }
        break;
      case "Escape":
        setSelected(new Set()); setFocus(null); break;
      case "a":
      case "A":
        if (e.ctrlKey || e.metaKey) { e.preventDefault(); setSelected(new Set(shown.map((r) => r.key))); }
        break;
      default:
    }
  };

  // ── drag & drop ──
  const dragKind = (e: DragEvent<HTMLElement>): "files" | "items" | null => {
    const t = Array.from(e.dataTransfer.types);
    if (t.includes(DRAG_TYPE)) return "items";
    if (t.includes("Files")) return "files";
    return null;
  };
  const expandTimer = useRef<{ path: string; t: number } | null>(null);
  const armExpand = (path: string) => {
    if (expanded.has(path) || expandTimer.current?.path === path) return;
    if (expandTimer.current) window.clearTimeout(expandTimer.current.t);
    expandTimer.current = {
      path,
      t: window.setTimeout(() => { setExpanded((s) => new Set(s).add(path)); expandTimer.current = null; }, 650),
    };
  };
  const handleDrop = (e: DragEvent<HTMLElement>, k: "files" | "items", folder: string | null, replaceDoc?: LibraryDoc) => {
    if (k === "items") {
      if (folder == null) return;
      let payload: { docs?: string[]; folders?: string[] } = {};
      try { payload = JSON.parse(e.dataTransfer.getData(DRAG_TYPE) || "{}"); } catch { /* not ours */ }
      void moveItems(payload.docs ?? [], payload.folders ?? [], folder);
      return;
    }
    if (replaceDoc) {
      const f = e.dataTransfer.files?.[0];
      if (f) askReplace(replaceDoc, f);
      return;
    }
    if (folder == null) return;
    const target = folder;
    void filesFromDrop(e.dataTransfer).then((picked) => {
      if (target === ROOT && picked.some((p) => !p.rel)) say("Loose files need a folder — open one, or drop them onto a folder.");
      enqueue(picked.filter((p) => target !== ROOT || p.rel).map((p) => ({ file: p.file, folder: p.rel ? joinPath(target, p.rel) : target })));
    });
  };
  const dropProps = (key: string, folder: string | null, replaceDoc?: LibraryDoc) => ({
    onDragOver: (e: DragEvent<HTMLElement>) => {
      const k = dragKind(e);
      if (!k) return;
      if (replaceDoc ? k !== "files" : folder == null) return;
      if (k === "items" && loc.kind === "archived") return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = k === "files" ? "copy" : "move";
      if (dropKeyRef.current !== key) { dropKeyRef.current = key; setDropKey(key); }
      if (key.startsWith("tree:") && folder) armExpand(folder);
    },
    onDragLeave: (e: DragEvent<HTMLElement>) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      if (dropKeyRef.current === key) { dropKeyRef.current = null; setDropKey(null); }
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      const k = dragKind(e);
      if (!k) return;
      e.preventDefault();
      e.stopPropagation();
      dropKeyRef.current = null;
      setDropKey(null);
      handleDrop(e, k, folder, replaceDoc);
    },
  });
  const dragStart = (e: DragEvent<HTMLElement>, r: Row) => {
    if (loc.kind === "archived" || renaming) { e.preventDefault(); return; }
    let keys: string[];
    if (selected.has(r.key)) keys = [...selected];
    else {
      keys = [r.key];
      setSelected(new Set([r.key]));
      setFocus(r.key);
      anchorRef.current = r.key;
    }
    const payload = {
      docs: keys.filter((k) => k.startsWith("d:")).map((k) => k.slice(2)),
      folders: keys.filter((k) => k.startsWith("f:")).map((k) => k.slice(2)),
    };
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "move";
    const ghost = document.createElement("div");
    ghost.textContent = keys.length === 1 ? (r.type === "folder" ? r.name : r.doc.filename) : `${keys.length} items`;
    ghost.style.cssText = "position:fixed;top:-200px;left:-200px;padding:6px 10px;border-radius:8px;background:#1e293b;color:#fff;font:600 12px system-ui,sans-serif;white-space:nowrap;max-width:280px;overflow:hidden;text-overflow:ellipsis";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 12, 12);
    window.setTimeout(() => ghost.remove(), 0);
  };

  // ── pieces ──
  const mainDropFolder = !searching && loc.kind === "folder" ? loc.path : null;
  const folderMissing = loc.kind === "folder" && !!loc.path && !!docsQ.data && !emptyFoldersQ.isLoading && !byPath.has(loc.path);
  const extraCol = searching || loc.kind !== "folder";
  const cols = extraCol
    ? "grid-cols-[1.5rem_minmax(0,1fr)_minmax(0,11rem)_6.5rem_6rem_5.5rem_2rem]"
    : "grid-cols-[1.5rem_minmax(0,1fr)_6.5rem_6rem_5.5rem_2rem]";
  const thumbPaths = useMemo(
    () => (view === "grid" ? shown.filter(isDocRow).filter((r) => r.doc.storage_path && kindOf(r.doc) === "image").slice(0, 80).map((r) => r.doc.storage_path as string) : []),
    [view, shown],
  );
  const thumbs = useThumbs(thumbPaths).data;
  const previewRow = selectedRows.length === 1 ? selectedRows[0] : null;
  const selectedBytes = selectedRows.filter(isDocRow).reduce((s, r) => s + (r.doc.size_bytes ?? 0), 0);

  const renderTree = (node: FolderNode, depth: number): ReactNode => {
    const open = expanded.has(node.path);
    const active = !searching && loc.kind === "folder" && loc.path === node.path;
    const k = `tree:${node.path}`;
    return (
      <li key={node.path || "root"}>
        <div {...dropProps(k, node.path)}
          className={cn(
            "group flex items-center gap-0.5 rounded-md pr-1.5 text-[13px] transition-colors",
            active ? "bg-brand-50 font-semibold text-brand-800" : "text-slate-700 hover:bg-slate-100",
            dropKey === k && "bg-brand-50 ring-2 ring-inset ring-brand-400",
          )}
          style={{ paddingLeft: 4 + depth * 14 }}>
          <button type="button" tabIndex={-1} onClick={() => toggleExpanded(node.path)}
            className={cn("flex h-6 w-5 shrink-0 items-center justify-center rounded text-slate-400 hover:text-slate-700", !node.children.length && "invisible")}
            aria-label={open ? "Collapse" : "Expand"}>
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          <button type="button" onClick={() => go({ kind: "folder", path: node.path })}
            className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left">
            {node.path === ROOT
              ? <Library className="h-4 w-4 shrink-0 text-slate-500" />
              : open && node.children.length
                ? <FolderOpen className="h-4 w-4 shrink-0 fill-amber-100 text-amber-500" />
                : <Folder className="h-4 w-4 shrink-0 fill-amber-100 text-amber-500" />}
            <span className="truncate">{node.name}</span>
          </button>
          {node.total > 0 && <span className="shrink-0 text-[11px] tabular-nums text-slate-400">{node.total}</span>}
        </div>
        {open && node.children.length > 0 && <ul>{node.children.map((c) => renderTree(c, depth + 1))}</ul>}
      </li>
    );
  };

  const crumbs = currentPath ? currentPath.split("/").map((seg, i, all) => ({ path: all.slice(0, i + 1).join("/"), name: seg })) : [];
  const crumbBtn = (path: string, label: ReactNode, last: boolean) => (
    <button type="button" key={path || "root"} onClick={() => go({ kind: "folder", path })} {...dropProps(`crumb:${path}`, path)}
      className={cn(
        "flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors",
        last ? "font-semibold text-slate-900" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800",
        dropKey === `crumb:${path}` && "bg-brand-50 ring-2 ring-inset ring-brand-400",
      )}>
      {label}
    </button>
  );

  const sortHead = (key: SortKey, label: string, right?: boolean) => (
    <button type="button" disabled={!sortable}
      onClick={() => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: key === "modified" || key === "size" ? -1 : 1 }))}
      className={cn("flex items-center gap-1 text-left font-semibold disabled:cursor-default", right && "justify-end", sortable && "hover:text-slate-800")}>
      {label}
      {sortable && sort.key === key && (sort.dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
    </button>
  );

  const nameOf = (r: Row) => (r.type === "folder" ? r.name : r.doc.filename);
  const metaOf = (r: Row) => (r.type === "folder" ? `${r.count} item${r.count === 1 ? "" : "s"}` : fmtBytes(r.doc.size_bytes));
  const rowDrop = (r: Row) => (r.type === "folder" ? dropProps(`row:${r.path}`, r.path) : driveOnly(r.doc) ? dropProps(`doc:${r.doc.id}`, null, r.doc) : {});
  const rowDropKey = (r: Row) => (r.type === "folder" ? `row:${r.path}` : `doc:${r.doc.id}`);
  const nameCell = (r: Row) =>
    renaming === r.key ? (
      <RenameInput initial={nameOf(r)} selectBase={r.type === "doc"} onDone={(v) => void commitRename(r, v)} onCancel={() => setRenaming(null)} />
    ) : (
      <span className="min-w-0">
        <span className={cn("block truncate", r.type === "folder" ? "font-medium text-slate-800" : "text-slate-800")}>{nameOf(r)}</span>
        {r.type === "doc" && r.snippet && (
          <span className="block truncate text-[11px] text-slate-400 [&_b]:font-semibold [&_b]:text-slate-600"
            dangerouslySetInnerHTML={{ __html: safeSnippet(r.snippet) }} />
        )}
      </span>
    );

  const empty = !docsQ.isLoading && !shown.length && !creatingFolder;

  return (
    <div className="relative flex h-[calc(100vh-15rem)] min-h-[560px] flex-col overflow-hidden rounded-2xl border border-hairline bg-white">
      {/* ── toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-3 py-2">
        <nav aria-label="Folder path" className="flex min-w-0 flex-1 items-center gap-0.5 text-[14px]">
          {crumbBtn(ROOT, <><Library className="h-4 w-4 shrink-0" /> Library</>, !searching && currentPath === ROOT)}
          {!searching && crumbs.map((c, i) => (
            <span key={c.path} className="flex min-w-0 items-center gap-0.5">
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
              {crumbBtn(c.path, <span className="truncate">{c.name}</span>, i === crumbs.length - 1)}
            </span>
          ))}
          {!searching && loc.kind !== "folder" && (
            <span className="flex items-center gap-0.5">
              <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
              <span className="px-1.5 font-semibold text-slate-900">{loc.kind === "recent" ? "Recent" : "Archived"}</span>
            </span>
          )}
          {searching && (
            <span className="flex min-w-0 items-center gap-0.5">
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
              <span className="truncate px-1.5 font-semibold text-slate-900">Search results for &ldquo;{q.trim()}&rdquo;</span>
            </span>
          )}
        </nav>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") setQ(""); }}
              placeholder="Search names and what's inside"
              className="h-8 w-64 rounded-full border border-hairline bg-slate-50/60 pl-8 pr-7 text-[13px] focus:border-brand-500 focus:bg-white focus:outline-none" />
            {q && (
              <button type="button" onClick={() => setQ("")} aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex h-8 items-center rounded-full border border-hairline p-0.5" role="group" aria-label="View">
            {([["list", List, "List"], ["grid", LayoutGrid, "Grid"]] as const).map(([v, Icon, label]) => (
              <button key={v} type="button" onClick={() => setView(v)} title={`${label} view`} aria-pressed={view === v}
                className={cn("flex h-7 w-8 items-center justify-center rounded-full transition-colors", view === v ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800")}>
                <Icon className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setShowPreview(!showPreview)} title={showPreview ? "Hide the preview" : "Show the preview"}
            aria-pressed={showPreview}
            className={cn("flex h-8 w-8 items-center justify-center rounded-full border border-hairline transition-colors", showPreview ? "bg-slate-100 text-slate-800" : "text-slate-500 hover:text-slate-800")}>
            {showPreview ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
          </button>
          <OverflowMenu align="end" items={folderActions()} trigger={(open) => (
            <span className={cn("inline-flex h-8 items-center gap-1.5 rounded-full bg-brand-700 px-3.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800", open && "bg-brand-800")}>
              <Plus className="h-4 w-4" /> New
            </span>
          )} />
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* ── folder tree ── */}
        <nav aria-label="Folders" className="hidden w-60 shrink-0 overflow-y-auto border-r border-hairline bg-slate-50/50 p-2 md:block">
          <ul>{renderTree(root, 0)}</ul>
          <div className="my-2 border-t border-hairline" />
          {([["recent", Clock, "Recent"], ["archived", Archive, "Archived"]] as const).map(([v, Icon, label]) => (
            <button key={v} type="button" onClick={() => go({ kind: v })}
              className={cn(
                "flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[13px] transition-colors",
                !searching && loc.kind === v ? "bg-brand-50 font-semibold text-brand-800" : "text-slate-600 hover:bg-slate-100",
              )}>
              <Icon className="h-4 w-4 text-slate-400" /> {label}
            </button>
          ))}
        </nav>

        {/* ── the folder's contents ── */}
        <section className="relative flex min-w-0 flex-1 flex-col" {...dropProps("main", mainDropFolder)}
          onContextMenu={emptyCtx}
          onClick={() => { setSelected(new Set()); setFocus(null); }}>
          {view === "list" && (
            <div className={cn("grid items-center gap-2 border-b border-hairline bg-slate-50/60 px-3 py-1.5 text-[11.5px] text-slate-500", cols)}
              onClick={(e) => e.stopPropagation()}>
              <span />
              {sortHead("name", "Name")}
              {extraCol && <span className="font-semibold">Folder</span>}
              {sortHead("modified", "Modified")}
              {sortHead("type", "Type")}
              {sortHead("size", "Size", true)}
              <span />
            </div>
          )}
          <div ref={listRef} tabIndex={0} role="listbox" aria-multiselectable="true" aria-label="Files and folders"
            onKeyDown={onListKey}
            className={cn("min-h-0 flex-1 overflow-y-auto outline-none", view === "grid" && "p-3")}>
            {docsQ.isLoading ? (
              <div className="space-y-1 p-3">{Array.from({ length: 8 }, (_, i) => <div key={i} className="h-8 animate-pulse rounded-md bg-slate-100" />)}</div>
            ) : folderMissing ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-sm text-slate-500">
                <FolderOpen className="h-10 w-10 text-slate-300" />
                This folder isn&rsquo;t here any more — it may have been renamed or moved.
                <Button size="sm" variant="secondary" onClick={() => go({ kind: "folder", path: ROOT })}>Go to the Library</Button>
              </div>
            ) : empty ? (
              <div className="flex h-full flex-col items-center justify-center gap-1.5 p-8 text-center">
                {searching ? (
                  <>
                    <Search className="h-9 w-9 text-slate-300" />
                    <p className="text-sm font-medium text-slate-600">Nothing matched &ldquo;{q.trim()}&rdquo;</p>
                    <p className="text-xs text-slate-400">Try fewer words, or a word from inside the document.</p>
                  </>
                ) : loc.kind === "archived" ? (
                  <>
                    <Archive className="h-9 w-9 text-slate-300" />
                    <p className="text-sm font-medium text-slate-600">Nothing archived</p>
                  </>
                ) : loc.kind === "recent" ? (
                  <p className="text-sm text-slate-500">Nothing has changed lately.</p>
                ) : (
                  <div className="flex flex-col items-center gap-1.5 rounded-2xl border-2 border-dashed border-slate-200 px-10 py-8">
                    <Upload className="h-8 w-8 text-slate-300" />
                    <p className="text-sm font-medium text-slate-600">This folder is empty</p>
                    <p className="text-xs text-slate-400">Drag files or folders here from your computer, or use New.</p>
                  </div>
                )}
              </div>
            ) : view === "list" ? (
              <div>
                {creatingFolder && (
                  <div className={cn("grid items-center gap-2 border-b border-slate-100 bg-brand-50/40 px-3 py-1.5 text-[13px]", cols)} onClick={(e) => e.stopPropagation()}>
                    <span />
                    <span className="flex min-w-0 items-center gap-2">
                      <Folder className="h-[18px] w-[18px] shrink-0 fill-amber-100 text-amber-500" />
                      <RenameInput initial="New folder" onDone={(v) => void commitNewFolder(v)} onCancel={() => setCreatingFolder(false)} />
                    </span>
                  </div>
                )}
                {shown.map((r) => {
                  const sel = selected.has(r.key);
                  const drive = r.type === "doc" && driveOnly(r.doc);
                  return (
                    <div key={r.key} data-key={r.key} role="option" aria-selected={sel}
                      draggable={loc.kind !== "archived" && renaming !== r.key}
                      onDragStart={(e) => dragStart(e, r)}
                      onClick={(e) => clickRow(e, r)}
                      onDoubleClick={() => openRow(r)}
                      onContextMenu={(e) => rowCtx(e, r)}
                      {...rowDrop(r)}
                      className={cn(
                        "group grid cursor-default select-none items-center gap-2 border-b border-slate-100 px-3 py-1.5 text-[13px]",
                        cols,
                        sel ? "bg-brand-50" : "hover:bg-slate-50",
                        focus === r.key && "shadow-[inset_2px_0_0_theme(colors.brand.500)]",
                        dropKey === rowDropKey(r) && "bg-brand-50 ring-2 ring-inset ring-brand-400",
                      )}>
                      <button type="button" tabIndex={-1} onClick={(e) => toggleCheck(e, r)} aria-label={sel ? "Unselect" : "Select"}
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded border transition-opacity",
                          sel ? "border-brand-600 bg-brand-600 text-white" : "border-slate-300 bg-white opacity-0 group-hover:opacity-100",
                          selected.size > 0 && "opacity-100",
                        )}>
                        {sel && <Check className="h-3 w-3" />}
                      </button>
                      <span className="flex min-w-0 items-center gap-2">
                        <TypeIcon row={r} />
                        {nameCell(r)}
                        {drive && (
                          <span title="Still a Google Drive link — drop the real file on this row to bring it into Pegasus"
                            className="shrink-0 rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700">Drive</span>
                        )}
                      </span>
                      {extraCol && (
                        <span className="truncate text-slate-500" title={r.type === "doc" ? prettyPath(r.doc.folder) : prettyPath(parentOf(r.path))}>
                          {r.type === "doc" ? prettyPath(r.doc.folder) : prettyPath(parentOf(r.path))}
                        </span>
                      )}
                      <span className="truncate text-slate-500" title={r.type === "doc" ? fmtFull(modifiedOf(r.doc)) : undefined}>
                        {r.type === "doc" ? fmtWhen(modifiedOf(r.doc)) : fmtWhen(r.modified)}
                      </span>
                      <span className="truncate text-slate-500">{r.type === "folder" ? "Folder" : KIND[kindOf(r.doc)].label}</span>
                      <span className="truncate text-right tabular-nums text-slate-500">{metaOf(r)}</span>
                      <span className={cn("flex justify-end opacity-0 transition-opacity group-hover:opacity-100", sel && "opacity-100")} onClick={(e) => e.stopPropagation()}>
                        <OverflowMenu size="xs" label="More" items={actionsFor(sel && selectedRows.length > 1 ? selectedRows : [r])} />
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-2">
                {creatingFolder && (
                  <div className="flex flex-col items-center gap-1.5 rounded-xl border border-brand-300 bg-brand-50/40 p-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex h-24 items-center justify-center"><Folder className="h-12 w-12 fill-amber-100 text-amber-500" /></div>
                    <RenameInput initial="New folder" onDone={(v) => void commitNewFolder(v)} onCancel={() => setCreatingFolder(false)} />
                  </div>
                )}
                {shown.map((r) => {
                  const sel = selected.has(r.key);
                  const thumb = r.type === "doc" && r.doc.storage_path ? thumbs?.get(r.doc.storage_path) : undefined;
                  const drive = r.type === "doc" && driveOnly(r.doc);
                  return (
                    <div key={r.key} data-key={r.key} role="option" aria-selected={sel}
                      draggable={loc.kind !== "archived" && renaming !== r.key}
                      onDragStart={(e) => dragStart(e, r)}
                      onClick={(e) => clickRow(e, r)}
                      onDoubleClick={() => openRow(r)}
                      onContextMenu={(e) => rowCtx(e, r)}
                      {...rowDrop(r)}
                      title={nameOf(r)}
                      className={cn(
                        "group relative flex cursor-default select-none flex-col rounded-xl border p-2 text-center transition-colors",
                        sel ? "border-brand-300 bg-brand-50" : "border-transparent hover:bg-slate-50",
                        dropKey === rowDropKey(r) && "border-brand-400 bg-brand-50 ring-2 ring-brand-400",
                      )}>
                      <button type="button" tabIndex={-1} onClick={(e) => toggleCheck(e, r)} aria-label={sel ? "Unselect" : "Select"}
                        className={cn(
                          "absolute left-2 top-2 z-10 flex h-4 w-4 items-center justify-center rounded border transition-opacity",
                          sel ? "border-brand-600 bg-brand-600 text-white" : "border-slate-300 bg-white opacity-0 group-hover:opacity-100",
                          selected.size > 0 && "opacity-100",
                        )}>
                        {sel && <Check className="h-3 w-3" />}
                      </button>
                      <div className="flex h-24 items-center justify-center overflow-hidden rounded-lg bg-slate-50">
                        {thumb ? <img src={thumb} alt="" loading="lazy" className="h-full w-full object-cover" /> : <TypeIcon row={r} big />}
                      </div>
                      <div className="mt-1.5 min-h-[2.2rem] text-[12.5px] leading-snug text-slate-800">
                        {renaming === r.key
                          ? <RenameInput initial={nameOf(r)} selectBase={r.type === "doc"} onDone={(v) => void commitRename(r, v)} onCancel={() => setRenaming(null)} />
                          : <span className="line-clamp-2 break-words">{nameOf(r)}</span>}
                      </div>
                      <div className="truncate text-[11px] text-slate-400">
                        {drive ? <span className="font-semibold text-amber-700">Still in Drive</span> : r.type === "folder" ? metaOf(r) : `${fmtWhen(modifiedOf(r.doc))}${r.doc.size_bytes ? ` · ${fmtBytes(r.doc.size_bytes)}` : ""}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* status line */}
          <div className="flex items-center gap-3 border-t border-hairline px-3 py-1.5 text-[12px] text-slate-500" onClick={(e) => e.stopPropagation()}>
            <span>{shown.length} item{shown.length === 1 ? "" : "s"}</span>
            {selected.size > 0 && <span>{selected.size} selected{selectedBytes ? ` · ${fmtBytes(selectedBytes)}` : ""}</span>}
            {mainDropFolder != null && (
              <span className="ml-auto hidden lg:flex">
                <InfoTip label="How to use the Library" align="end">Drag files or whole folders from your computer into this window to upload them. Drag items onto a folder, here or on the left, to move them. Double-click to open; right-click for more.</InfoTip>
              </span>
            )}
          </div>
          {dropKey === "main" && mainDropFolder != null && (
            <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-brand-400 bg-brand-50/70">
              <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand-800 shadow-e2">
                <Upload className="h-4 w-4" />
                {mainDropFolder ? <>Drop to upload to {leafOf(mainDropFolder)}</> : <>Drop a folder to add it to the Library</>}
              </div>
            </div>
          )}
        </section>

        {/* ── preview ── */}
        {showPreview && (
          <aside className="hidden w-[380px] shrink-0 overflow-y-auto border-l border-hairline lg:block">
            {previewRow ? (
              previewRow.type === "doc" ? (
                <DocPreview doc={previewRow.doc} archived={loc.kind === "archived"}
                  actions={actionsFor([previewRow])}
                  onOpen={() => openRow(previewRow)}
                  onDownload={() => void download([previewRow.doc])}
                  onRestore={() => void restore([previewRow.doc])}
                  onEdit={() => setNoteEdit({ id: previewRow.doc.id })}
                  onPickReplace={() => pickReplace(previewRow.doc)}
                  onGoFolder={(p) => go({ kind: "folder", path: p })}
                  replaceDrop={dropProps(`pane:${previewRow.doc.id}`, null, previewRow.doc)}
                  dropActive={dropKey === `pane:${previewRow.doc.id}`} />
              ) : (
                <div className="flex flex-col items-center gap-3 p-6 text-center">
                  <Folder className="h-16 w-16 fill-amber-100 text-amber-500" />
                  <div>
                    <div className="text-[15px] font-semibold text-slate-900">{previewRow.name}</div>
                    <div className="text-xs text-slate-500">
                      {previewRow.count} item{previewRow.count === 1 ? "" : "s"}{previewRow.modified ? ` · last change ${fmtWhen(previewRow.modified)}` : ""}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => go({ kind: "folder", path: previewRow.path })}>Open</Button>
                    <Button size="sm" variant="secondary" onClick={() => setRenaming(previewRow.key)}>Rename</Button>
                    <Button size="sm" variant="secondary" onClick={() => setMoveOpen({ docs: [], folders: [previewRow.path] })}>Move…</Button>
                  </div>
                </div>
              )
            ) : selectedRows.length > 1 ? (
              <div className="p-5">
                <div className="text-[15px] font-semibold text-slate-900">{selectedRows.length} items selected</div>
                {selectedBytes > 0 && <div className="text-xs text-slate-500">{fmtBytes(selectedBytes)}</div>}
                <ul className="mt-3 space-y-1">
                  {selectedRows.slice(0, 10).map((r) => (
                    <li key={r.key} className="flex items-center gap-2 text-[13px] text-slate-700"><TypeIcon row={r} /><span className="truncate">{nameOf(r)}</span></li>
                  ))}
                  {selectedRows.length > 10 && <li className="text-xs text-slate-400">and {selectedRows.length - 10} more</li>}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2">
                  {loc.kind === "archived" ? (
                    <Button size="sm" onClick={() => void restore(selectedRows.filter(isDocRow).map((r) => r.doc))}>Restore</Button>
                  ) : (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => setMoveOpen({ docs: selectedRows.filter(isDocRow).map((r) => r.doc.id), folders: selectedRows.filter(isFolderRow).map((r) => r.path) })}>Move…</Button>
                      <Button size="sm" variant="secondary" onClick={() => void download(selectedRows.filter(isDocRow).map((r) => r.doc))}>Download</Button>
                      <Button size="sm" variant="ghost" onClick={() => archiveItems(selectedRows.filter(isDocRow).map((r) => r.doc.id), selectedRows.filter(isFolderRow).map((r) => r.path))}>Archive</Button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 p-6 text-center">
                {loc.kind === "folder" ? <FolderOpen className="h-14 w-14 fill-amber-50 text-amber-400" /> : loc.kind === "recent" ? <Clock className="h-12 w-12 text-slate-300" /> : <Archive className="h-12 w-12 text-slate-300" />}
                <div className="flex items-center gap-2 text-[15px] font-semibold text-slate-900">
                  {searching ? "Search" : loc.kind === "folder" ? (currentPath ? leafOf(currentPath) : "Library") : loc.kind === "recent" ? "Recent" : "Archived"}
                  {!searching && loc.kind === "archived" && (
                    <InfoTip label="About Archived">Archived documents are hidden from the Library, the phones and Ask Pegasus until someone restores them.</InfoTip>
                  )}
                </div>
                <div className="text-xs text-slate-500">{shown.length} item{shown.length === 1 ? "" : "s"}</div>
                <p className="mt-3 text-[12.5px] leading-relaxed text-slate-500">
                  {loc.kind === "archived" ? "Pick a document to restore it." : "Pick a file to preview it here."}
                </p>
              </div>
            )}
          </aside>
        )}
      </div>

      {/* ── upload tray ── */}
      {jobs.length > 0 && (
        <div className="absolute bottom-3 right-3 z-20 w-80 overflow-hidden rounded-xl border border-hairline bg-white shadow-e3">
          <div className="flex items-center gap-2 border-b border-hairline px-3 py-2 text-[13px] font-semibold text-slate-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin text-brand-600" /> : failed ? <TriangleAlert className="h-4 w-4 text-amber-500" /> : <Check className="h-4 w-4 text-emerald-600" />}
            <span className="flex-1">
              {busy
                ? `Uploading ${jobs.filter((j) => j.state === "done" || j.state === "fail").length + 1} of ${jobs.length}`
                : failed ? `${failed} didn't upload` : `${jobs.length} uploaded`}
            </span>
            <button type="button" onClick={() => setTrayOpen(!trayOpen)} className="rounded p-0.5 text-slate-400 hover:text-slate-700" aria-label={trayOpen ? "Collapse" : "Expand"}>
              {trayOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {!busy && (
              <button type="button" onClick={() => setJobs([])} className="rounded p-0.5 text-slate-400 hover:text-slate-700" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {trayOpen && (
            <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto">
              {jobs.map((j) => (
                <li key={j.id} className="flex items-center gap-2 px-3 py-1.5 text-[12px]">
                  {j.state === "up" ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-brand-600" />
                    : j.state === "done" ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                      : j.state === "fail" ? <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                        : <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-slate-300" />}
                  <span className="min-w-0 flex-1 truncate text-slate-700" title={j.name}>{j.name}</span>
                  <span className={cn("max-w-[45%] shrink-0 truncate", j.state === "fail" ? "text-amber-700" : "text-slate-400")} title={j.note ?? prettyPath(j.folder)}>
                    {j.note ?? leafOf(j.folder)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── toast ── */}
      {toast && (
        <div className="absolute bottom-12 left-1/2 z-30 flex -translate-x-1/2 items-center gap-3 rounded-full bg-slate-900 px-4 py-2 text-[13px] text-white shadow-e3">
          <span>{toast.text}</span>
          {toast.undo && (
            <button type="button" onClick={() => { const u = toast.undo; setToast(null); u?.(); }}
              className="font-semibold text-sky-300 hover:text-sky-200">Undo</button>
          )}
          <button type="button" onClick={() => setToast(null)} className="text-white/50 hover:text-white" aria-label="Dismiss"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {ctx && (
        <ContextMenu x={ctx.x} y={ctx.y} onClose={closeCtx}
          items={ctx.empty ? folderActions() : actionsFor(selectedRows.length ? selectedRows : focusRow ? [focusRow] : [])} />
      )}

      {/* hidden pickers */}
      <input ref={filesInput} type="file" multiple className="hidden" onChange={(e) => {
        const list = Array.from(e.target.files ?? []);
        e.target.value = "";
        if (currentPath) enqueue(list.map((file) => ({ file, folder: currentPath })));
      }} />
      <input ref={folderInput} type="file" multiple className="hidden" {...({ webkitdirectory: "", directory: "" } as Record<string, string>)} onChange={(e) => {
        const picked = filesFromInput(e.target.files);
        e.target.value = "";
        const base = currentPath ?? ROOT;
        enqueue(picked.map((p) => ({ file: p.file, folder: p.rel ? joinPath(base, p.rel) : base })));
      }} />
      <input ref={replaceInput} type="file" className="hidden" onChange={(e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        const d = replaceFor.current;
        replaceFor.current = null;
        if (f && d) askReplace(d, f);
      }} />

      {/* same-name files */}
      <Modal open={conflicts.length > 0} onClose={() => resolveConflicts(false)} size="md"
        icon={<RefreshCw className="h-5 w-5 text-brand-700" />}
        title={
          <span className="flex items-center gap-2">
            {conflicts.length === 1 ? "This file is already in the Library" : `${conflicts.length} files are already in the Library`}
            <InfoTip label="What Replace does">Replace keeps one copy: the new file becomes the next version and everyone sees it right away. The old file stays in history.</InfoTip>
          </span>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            {conflicts.length > 1 && (
              <>
                <Button size="sm" variant="ghost" onClick={() => setConflicts((cs) => cs.map((c) => ({ ...c, choice: "replace" })))}>Replace all</Button>
                <Button size="sm" variant="ghost" onClick={() => setConflicts((cs) => cs.map((c) => ({ ...c, choice: "skip" })))}>Skip all</Button>
              </>
            )}
            <span className="ml-auto" />
            <Button size="sm" variant="secondary" onClick={() => resolveConflicts(false)}>Cancel</Button>
            <Button size="sm" onClick={() => resolveConflicts(true)}>Continue</Button>
          </div>
        }>
        <ul className="divide-y divide-slate-100">
          {conflicts.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-2.5">
              <TypeIcon row={{ type: "doc", key: "", doc: { ...c.doc, filename: c.file.name, mime: c.file.type || c.doc.mime } }} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-800">{c.file.name}</div>
                <div className="truncate text-xs text-slate-500">
                  {driveOnly(c.doc) ? "Still a Google Drive link in " : "Already in "}{prettyPath(c.doc.folder)}{driveOnly(c.doc) ? "" : ` · v${c.doc.version}`}
                  {c.doc.filename !== c.file.name && <> · as &ldquo;{c.doc.filename}&rdquo;</>}
                </div>
              </div>
              <select value={c.choice}
                onChange={(e) => { const v = e.target.value as Conflict["choice"]; setConflicts((cs) => cs.map((x) => (x.id === c.id ? { ...x, choice: v } : x))); }}
                className="rounded-md border border-hairline px-2 py-1 text-sm focus:border-brand-500 focus:outline-none">
                <option value="replace">{driveOnly(c.doc) ? "Bring it in" : `Replace (v${c.doc.version + 1})`}</option>
                <option value="both">Keep both</option>
                <option value="skip">Skip</option>
              </select>
            </li>
          ))}
        </ul>
      </Modal>

      {/* move to… */}
      {moveOpen && (
        <MoveDialog root={root} moving={moveOpen} startAt={currentPath ?? ROOT}
          onClose={() => setMoveOpen(null)}
          onMove={(target) => { const m = moveOpen; setMoveOpen(null); void moveItems(m.docs, m.folders, target); }} />
      )}

      {/* archive a folder with things in it */}
      <Modal open={!!archiveAsk} onClose={() => setArchiveAsk(null)} size="sm"
        icon={<Archive className="h-5 w-5 text-rose-600" />}
        title={
          <span className="flex items-center gap-2">
            {archiveAsk && archiveAsk.folders.length === 1 ? `Archive the folder “${leafOf(archiveAsk.folders[0])}”?` : "Archive these folders?"}
            <InfoTip label="What archiving does">Archived documents leave the Library, the phones and Ask Pegasus. You can restore any of them from Archived.</InfoTip>
          </span>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            <span className="ml-auto" />
            <Button size="sm" variant="secondary" onClick={() => setArchiveAsk(null)}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={() => { const a = archiveAsk; setArchiveAsk(null); if (a) void doArchive(a.docs, a.folders); }}>
              Archive {archiveAsk?.docs.length ?? 0} document{archiveAsk?.docs.length === 1 ? "" : "s"}
            </Button>
          </div>
        }>
        <p className="text-sm text-slate-600">
          Everything inside — {archiveAsk?.docs.length ?? 0} document{archiveAsk?.docs.length === 1 ? "" : "s"}, subfolders included — moves to Archived.
        </p>
      </Modal>

      <NoteEditorModal open={!!noteEdit} noteId={noteEdit?.id ?? null} onClose={() => setNoteEdit(null)}
        initialFolder={currentPath || NOTES_FOLDER} folders={allFolderPaths}
        onSaved={(id) => { pendingFocus.current = `d:${id}`; say("Note saved — the Library, the phones and Ask Pegasus have it now."); }} />
      <NoteViewerModal noteId={noteView} onClose={() => setNoteView(null)}
        onEdit={() => { const id = noteView; setNoteView(null); setNoteEdit({ id }); }} />
    </div>
  );
}

// ── the preview pane for one document ─────────────────────────────────────────
function DocPreview({
  doc, archived, actions, onOpen, onDownload, onRestore, onEdit, onPickReplace, onGoFolder, replaceDrop, dropActive,
}: {
  doc: LibraryDoc;
  archived: boolean;
  actions: MenuEntry[];
  onOpen: () => void;
  onDownload: () => void;
  onRestore: () => void;
  onEdit: () => void;
  onPickReplace: () => void;
  onGoFolder: (path: string) => void;
  replaceDrop: {
    onDragOver: (e: DragEvent<HTMLElement>) => void;
    onDragLeave: (e: DragEvent<HTMLElement>) => void;
    onDrop: (e: DragEvent<HTMLElement>) => void;
  };
  dropActive: boolean;
}) {
  const k = kindOf(doc);
  const ext = extOf(doc.filename);
  const drive = driveOnly(doc);
  const isNote = k === "note";
  const media = !!doc.storage_path && (k === "pdf" || k === "image" || k === "audio" || (k === "video" && PLAYABLE_VIDEO.has(ext)));
  const urlQ = useQuery({
    queryKey: ["library_preview_url", doc.storage_path],
    enabled: media,
    staleTime: 8 * 60 * 1000,
    queryFn: () => libraryDocUrl(doc.storage_path as string),
  });
  const wantsText = !media && (isNote || !!doc.content_extracted_at);
  const textQ = useLibraryDocText(wantsText ? doc.id : null);
  const text = textQ.data ?? null;
  const Icon = KIND[k].icon;
  const changed = doc.updated_at && Math.abs(new Date(doc.updated_at).getTime() - new Date(doc.created_at).getTime()) > 60_000;

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex items-start gap-2 border-b border-hairline p-4">
        <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", KIND[k].cls)} />
        <div className="min-w-0 flex-1">
          <div className="break-words text-[14px] font-semibold leading-snug text-slate-900">{doc.filename}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {KIND[k].label}{doc.size_bytes && !isNote ? ` · ${fmtBytes(doc.size_bytes)}` : ""}{doc.version > 1 ? ` · v${doc.version}` : ""}
          </div>
        </div>
        {!archived && <OverflowMenu size="xs" label="More" items={actions} />}
      </div>

      <div className="flex flex-wrap gap-2 px-4 pt-3">
        {archived ? (
          <Button size="sm" onClick={onRestore}><ArchiveRestore className="mr-1 h-3.5 w-3.5" /> Restore</Button>
        ) : isNote ? (
          <Button size="sm" onClick={onEdit}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit note</Button>
        ) : (
          <Button size="sm" onClick={onOpen}><ExternalLink className="mr-1 h-3.5 w-3.5" /> {drive ? "Open in Google Drive" : "Open"}</Button>
        )}
        {doc.storage_path && <Button size="sm" variant="secondary" onClick={onDownload}><Download className="mr-1 h-3.5 w-3.5" /> Download</Button>}
      </div>

      <div className="p-4">
        {drive ? (
          <div {...replaceDrop}
            className={cn("flex flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors",
              dropActive ? "border-brand-400 bg-brand-50" : "border-amber-200 bg-amber-50/40")}>
            <Upload className="h-7 w-7 text-amber-500" />
            <p className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
              Still lives in Google Drive
              <InfoTip label="Why bring the file in">Once the real file is in Pegasus, the phones and Ask Pegasus can read it.</InfoTip>
            </p>
            <p className="text-xs leading-relaxed text-slate-500">Drop the real file here to bring it in.</p>
            <Button size="sm" variant="secondary" onClick={onPickReplace}>Choose the file…</Button>
          </div>
        ) : media ? (
          !urlQ.data ? (
            <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
          ) : k === "pdf" ? (
            <iframe title={doc.filename} src={`${urlQ.data}#view=FitH`} className="h-[62vh] min-h-[420px] w-full rounded-xl border border-hairline bg-slate-50" />
          ) : k === "image" ? (
            <img src={urlQ.data} alt={doc.filename} className="max-h-[60vh] w-full rounded-xl border border-hairline bg-slate-50 object-contain" />
          ) : k === "video" ? (
            <video src={urlQ.data} controls className="w-full rounded-xl bg-black" />
          ) : (
            <audio src={urlQ.data} controls className="w-full" />
          )
        ) : text ? (
          <div>
            {!isNote && (
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] text-slate-400">
                Text Preview
                <InfoTip label="About the text preview">The words Pegasus read from this file. Open it to see the real layout.</InfoTip>
              </p>
            )}
            <div className="max-h-[55vh] overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-hairline bg-slate-50/60 p-3 text-[12.5px] leading-relaxed text-slate-700">
              {text.slice(0, 12000)}{text.length > 12000 ? " …" : ""}
            </div>
          </div>
        ) : wantsText && textQ.isLoading ? (
          <div className="h-40 animate-pulse rounded-xl bg-slate-100" />
        ) : (
          <div className="flex flex-col items-center gap-2 rounded-xl bg-slate-50 px-4 py-8 text-center">
            <Icon className={cn("h-10 w-10", KIND[k].cls)} />
            <p className="text-sm text-slate-600">{k === "video" ? `${ext.toUpperCase()} videos don't play in a browser — download it to watch.` : "No preview for this kind of file."}</p>
            {!!doc.storage_path && <p className="text-xs text-slate-400">Open or download it to see it.</p>}
          </div>
        )}
      </div>

      <dl className="mt-auto grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-2 gap-y-1.5 border-t border-hairline p-4 text-[12.5px]">
        <dt className="text-slate-400">Folder</dt>
        <dd><button type="button" onClick={() => onGoFolder(doc.folder)} className="text-left text-brand-700 hover:underline">{prettyPath(doc.folder)}</button></dd>
        <dt className="text-slate-400">Added</dt>
        <dd className="text-slate-700">{fmtFull(doc.created_at)}{doc.created_by ? ` · ${shortWho(doc.created_by)}` : ""}</dd>
        {changed && (
          <>
            <dt className="text-slate-400">Changed</dt>
            <dd className="text-slate-700">{fmtFull(doc.updated_at)}{doc.updated_by ? ` · ${shortWho(doc.updated_by)}` : ""}</dd>
          </>
        )}
        {doc.version > 1 && (
          <>
            <dt className="text-slate-400">Version</dt>
            <dd className="text-slate-700">v{doc.version} — the older files are kept</dd>
          </>
        )}
        <dt className="text-slate-400">Ask Pegasus</dt>
        <dd className="text-slate-700">
          {isNote ? "Reads notes first" : doc.content_extracted_at ? "Can read what's inside" : drive ? "Can't read it yet (still in Drive)" : "Can't read inside this file"}
        </dd>
      </dl>
    </div>
  );
}

// ── Move to… ─────────────────────────────────────────────────────────────────
function MoveDialog({ root, moving, startAt, onClose, onMove }: {
  root: FolderNode;
  moving: { docs: string[]; folders: string[] };
  startAt: string;
  onClose: () => void;
  onMove: (path: string) => void;
}) {
  const [pick, setPick] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(() => {
    const s = new Set<string>([ROOT]);
    for (let p = startAt; p; p = parentOf(p)) s.add(p);
    return s;
  });
  const blocked = (p: string) => moving.folders.some((f) => isInside(p, f));
  const count = moving.docs.length + moving.folders.length;
  const rootOk = moving.docs.length === 0; // documents always live in a folder
  const node = (n: FolderNode, depth: number): ReactNode => {
    const isOpen = open.has(n.path);
    const off = blocked(n.path) || (n.path === ROOT && !rootOk);
    return (
      <li key={n.path || "root"}>
        <div className={cn("flex items-center gap-0.5 rounded-md pr-2 text-[13px]",
          pick === n.path ? "bg-brand-50 font-semibold text-brand-800" : off ? "text-slate-300" : "text-slate-700 hover:bg-slate-100")}
          style={{ paddingLeft: 4 + depth * 14 }}>
          <button type="button" onClick={() => setOpen((s) => { const x = new Set(s); if (x.has(n.path)) x.delete(n.path); else x.add(n.path); return x; })}
            className={cn("flex h-6 w-5 items-center justify-center text-slate-400", !n.children.length && "invisible")}>
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          <button type="button" disabled={off} onClick={() => setPick(n.path)} onDoubleClick={() => { if (!off) onMove(n.path); }}
            className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left disabled:cursor-not-allowed">
            {n.path === ROOT ? <Library className="h-4 w-4 shrink-0" /> : <Folder className={cn("h-4 w-4 shrink-0", off ? "text-slate-300" : "fill-amber-100 text-amber-500")} />}
            <span className="truncate">{n.name}</span>
          </button>
        </div>
        {isOpen && n.children.length > 0 && <ul>{n.children.map((c) => node(c, depth + 1))}</ul>}
      </li>
    );
  };
  return (
    <Modal open onClose={onClose} size="sm" icon={<FolderInput className="h-5 w-5 text-brand-700" />}
      title={`Move ${count} item${count === 1 ? "" : "s"} to…`}
      footer={
        <div className="flex w-full items-center gap-2">
          <span className="truncate text-xs text-slate-500">{pick != null ? prettyPath(pick) : "Pick a folder"}</span>
          <span className="ml-auto" />
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={pick == null} onClick={() => { if (pick != null) onMove(pick); }}>Move here</Button>
        </div>
      }>
      <ul className="max-h-[50vh] overflow-y-auto">{node(root, 0)}</ul>
    </Modal>
  );
}
